import React, { useEffect, useState } from 'react';
import { sendSitewideBannerEvent } from 'snowplow';

import CloseSmall from 'ggApp/shared/icons/CloseSmall';
import { getOrderFromCookiesBasedStore as getOrderFromCookies } from 'ggApp/shared/context/Order/cookies';
import { Locale } from 'ggApp/types/common';

import { Wrap, BannerText, CloseButton } from './styled';

export type SitewideBannerProps = {
    sitewideBannerData?: {
        variantKey?: string;
        variantAttachment?: {
            text?: {
                [key in Locale]: string;
            };
        };
    };
};

export type EnhancedProps = {
    activeStore: {
        activeLanguage: string;
        store_id: number;
        code: string;
    };
    userId?: number | null;
    initDone?: boolean;
};

export const SitewideBanner = ({
    sitewideBannerData,
    activeStore,
    userId,
    initDone,
}: SitewideBannerProps & EnhancedProps): JSX.Element | null => {
    const [showBanner, setShowBanner] = useState(false);

    const bannerEnabled = sitewideBannerData?.variantKey !== 'disabled';
    const bannerText =
        sitewideBannerData?.variantAttachment?.text?.[activeStore.activeLanguage as Locale] ?? '';

    const setLocalStorageEntry = (value: { expiry: Date; value: boolean }) =>
        localStorage.setItem('showSitewideBanner', JSON.stringify(value));

    const getLocalStore = () => {
        const defaultValue = { expiry: null, value: true };
        if (typeof window === 'undefined') return defaultValue;
        const actualValue = JSON.parse(localStorage.getItem('showSitewideBanner') || '{}');
        if (typeof actualValue.value === 'undefined') return defaultValue;
        return actualValue;
    };

    useEffect(() => {
        let showSitewideBannerEntry = getLocalStore();

        const hasExpired =
            showSitewideBannerEntry.expiry &&
            Date.now() > Date.parse(showSitewideBannerEntry.expiry);

        if (hasExpired) {
            showSitewideBannerEntry = { expiry: null, value: true };
        }

        setShowBanner(showSitewideBannerEntry.value);
        setLocalStorageEntry(showSitewideBannerEntry);
    }, [bannerEnabled]);

    const closeBanner = () => {
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + 14);

        setLocalStorageEntry({ expiry, value: false });
        setShowBanner(false);
    };

    const handleBannerClick = () => {
        const orderID = getOrderFromCookies(activeStore.code) ?? null;
        const userID = userId ?? null;
        const eventPayload = {
            eventAction: 'click',
            eventLabel: bannerText,
            eventProperty: {
                store: activeStore.store_id,
                orderID,
                userID,
                bannerType: 'siteWideBanner',
            },
        };
        sendSitewideBannerEvent(eventPayload);
    };

    useEffect(() => {
        if (bannerEnabled && bannerText && initDone && showBanner) {
            const orderID = getOrderFromCookies(activeStore.code) ?? null;
            const userID = userId ?? null;
            const eventPayload = {
                eventAction: 'view',
                eventLabel: bannerText,
                eventProperty: {
                    store: activeStore.store_id,
                    orderID,
                    userID,
                    bannerType: 'siteWideBanner',
                },
            };
            sendSitewideBannerEvent(eventPayload);
        }
    }, [
        activeStore.code,
        activeStore.store_id,
        bannerEnabled,
        showBanner,
        bannerText,
        initDone,
        userId,
    ]);

    if (bannerEnabled && bannerText) {
        return showBanner ? (
            <Wrap>
                <BannerText onClick={handleBannerClick}>{bannerText}</BannerText>
                <CloseButton onClick={closeBanner}>
                    <CloseSmall />
                </CloseButton>
            </Wrap>
        ) : null;
    }
    return null;
};
